import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Chip, Paper, Button,
  Avatar, Divider, CircularProgress, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, Alert, Snackbar, IconButton, Collapse,
  TableSortLabel, useMediaQuery, useTheme, Tabs, Tab, Checkbox,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BuildIcon from '@mui/icons-material/Build';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SpeedIcon from '@mui/icons-material/Speed';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FilterListIcon from '@mui/icons-material/FilterList';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import { formatPortalDate } from '../utils/dateFormat';
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import WarningIcon from '@mui/icons-material/Warning';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import NotificationsPausedIcon from '@mui/icons-material/NotificationsPaused';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import api, { truckService, partService } from '../api/api';
import EditTruckDialog from './EditTruckDialog';
import AddPartDialog from './AddPartDialog';
import EditPartDialog from './EditPartDialog';
import InvoiceAttachmentList from './InvoiceAttachmentList';
import TruckServiceLog from './TruckServiceLog';
import TruckGloveBox from './TruckGloveBox';
import TruckConsumables from './TruckConsumables';
import TruckInspectionLogs from './TruckInspectionLogs';
import TruckWashes from './TruckWashes';
import { isFavoriteTruck, toggleFavoriteTruck } from '../utils/truckPrefs';
import ConfirmDialog from './ConfirmDialog';

const LAST_VIEWED_TRUCK_KEY = 'biofleet_last_viewed_truck_id_v1';
const TRUCK_RETURN_PATH_KEY = 'biofleet_truck_return_path_v1';
const VALID_MAIN_TABS = ['overview', 'service', 'consumables', 'gloveBox', 'inspections', 'washes'];

function formatMaybeNumber(value) {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
}

function formatFuelTankCapacities(raw) {
  if (raw === null || raw === undefined || raw === '') return 'N/A';

  let capacities = raw;
  if (typeof raw === 'string') {
    try {
      capacities = JSON.parse(raw);
    } catch {
      return raw;
    }
  }

  if (!Array.isArray(capacities)) return String(capacities);
  if (capacities.length === 0) return 'N/A';

  const formatted = capacities
    .map((c) => (c === null || c === undefined || c === '' ? null : String(c)))
    .filter(Boolean)
    .map((c) => `${c} L`)
    .join(', ');

  return formatted || 'N/A';
}

function resolveOther(value, otherValue) {
  if (value === null || value === undefined || value === '') return 'N/A';
  const normalized = String(value).trim().toLowerCase();
  if (normalized !== 'other') return String(value);
  if (otherValue === null || otherValue === undefined || otherValue === '') return 'Other';
  return String(otherValue);
}

function parseChecklist(raw) {
  if (!raw) return [];
  const lines = String(raw)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  return lines.map((line) => {
    const m = line.match(/^\s*(?:-|\*)?\s*\[(x| )\]\s*(.*)$/i);
    if (m) return { checked: m[1].toLowerCase() === 'x', text: (m[2] || '').trim() };
    return { checked: false, text: line.replace(/^\s*(?:-|\*)\s+/, '').trim() };
  }).filter((i) => i.text.length > 0);
}

function serializeChecklist(items) {
  const list = Array.isArray(items) ? items : [];
  return list
    .map((i) => `- [${i.checked ? 'x' : ' '}] ${String(i.text || '').trim()}`.trim())
    .filter((l) => l !== '- [ ]' && l !== '- [x]')
    .join('\n');
}

function normalizeFileUrl(value) {
  if (!value) return null;
  const url = String(value).trim();
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return url;
  if (url.startsWith('api/')) return `/${url}`;
  return url;
}

function PartRow({ part, allTrucks, onAssign, onEdit, onDelete, getTruckName, getConditionColor, getConditionLabel }) {
  const theme = useTheme();
  const partPhotoDialogFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [assignTo, setAssignTo] = useState(part.unit_intended_for || '');
  const [partPhotos, setPartPhotos] = useState([]);
  const [partPhotoDialogOpen, setPartPhotoDialogOpen] = useState(false);
  const [partPhotoDialogIndex, setPartPhotoDialogIndex] = useState(0);
  const [partPhotoZoom, setPartPhotoZoom] = useState(1);
  const [movements, setMovements] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const resolvedPartPhotos = useMemo(() => {
    const gallery = (Array.isArray(partPhotos) ? partPhotos : []).filter((photo) => photo?.url);
    const cover = part.photo_url ? { id: 'cover', url: part.photo_url, original_name: 'Cover photo' } : null;
    if (!cover) return gallery;
    return gallery.some((photo) => photo.url === cover.url) ? gallery : [cover, ...gallery];
  }, [part.photo_url, partPhotos]);

  const selectedPartPhotoUrl = resolvedPartPhotos[partPhotoDialogIndex]?.url || null;

  const fetchMovements = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const res = await fetch(`/api/parts/${part.id}/history`);
      const data = await res.json();
      setMovements(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching movements:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [part.id]);

  const fetchPartPhotos = useCallback(async () => {
    try {
      const res = await partService.getPhotos(part.id);
      setPartPhotos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching part photos:', err);
      setPartPhotos([]);
    }
  }, [part.id]);

  useEffect(() => {
    if (!open) return undefined;
    const refreshOpenRow = () => {
      fetchMovements();
      fetchPartPhotos();
    };

    refreshOpenRow();
    window.addEventListener('partsUpdated', refreshOpenRow);
    window.addEventListener('dataUpdated', refreshOpenRow);
    return () => {
      window.removeEventListener('partsUpdated', refreshOpenRow);
      window.removeEventListener('dataUpdated', refreshOpenRow);
    };
  }, [open, fetchMovements, fetchPartPhotos]);

  const openPartPhotoDialog = (index = 0) => {
    setPartPhotoDialogIndex(index);
    setPartPhotoZoom(1);
    setPartPhotoDialogOpen(true);
  };

  const handleAssign = async (newTruckId) => {
    const nextTruckId = newTruckId === '' ? null : parseInt(String(newTruckId), 10);
    setAssignTo(nextTruckId || '');
    await onAssign(part.id, nextTruckId);
    fetchMovements();
  };

  return (
    <>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          '& .MuiTableCell-root': { py: 1.4 },
          '&:hover': { backgroundColor: '#f5f5f5' },
          cursor: 'pointer'
        }}
      >
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {part.sku}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#001f3f' }}>
            {part.part_type}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {part.make} {part.model}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 700, color: part.part_number_oem ? '#001f3f' : '#9e9e9e' }}>
            {part.part_number_oem || '—'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 700, color: part.serial_number ? '#001f3f' : '#9e9e9e' }}>
            {part.serial_number || '—'}
          </Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOnIcon sx={{ fontSize: 18, color: '#1976d2' }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {part.location}
            </Typography>
          </Box>
        </TableCell>
        <TableCell align="center">
          <Chip
            label={part.quantity ?? 0}
            size="small"
            sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', minWidth: 48 }}
          />
        </TableCell>
        <TableCell align="center">
          <Chip
            label={`${part.condition_scale}/5`}
            size="small"
            sx={{
              backgroundColor: getConditionColor(part.condition_scale),
              color: 'white',
              fontWeight: 'bold',
              minWidth: 60
            }}
          />
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            {getConditionLabel(part.condition_scale)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Chip
            label={part.used_new ? 'New' : 'Used'}
            size="small"
            sx={{
              backgroundColor: part.used_new ? '#c8e6c9' : '#ffccbc',
              color: part.used_new ? '#2e7d32' : '#d84315',
              fontWeight: 'bold'
            }}
          />
        </TableCell>
        <TableCell align="right">
          {part.current_value ? (
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
              ${parseFloat(part.current_value).toFixed(2)}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: '#9e9e9e', fontStyle: 'italic' }}>
              —
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip
            label={getTruckName(part.unit_came_off_from, 'Shelf')}
            size="small"
            sx={{
              backgroundColor: part.unit_came_off_from ? '#e3f2fd' : '#f5f5f5',
              color: part.unit_came_off_from ? '#1565c0' : '#999'
            }}
          />
        </TableCell>
        <TableCell sx={{ minWidth: 220 }}>
          <FormControl fullWidth size="small">
            <Select
              value={assignTo}
              onChange={(e) => handleAssign(e.target.value)}
              displayEmpty
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Not Assigned</em>
              </MenuItem>
              {allTrucks.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name || t.name || `${t.make} ${t.model}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onEdit?.(part);
              }}
              sx={{ color: '#1976d2', '&:hover': { backgroundColor: '#e3f2fd' } }}
              aria-label="Edit part"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onDelete?.(part);
              }}
              sx={{ color: '#d32f2f', '&:hover': { backgroundColor: '#ffebee' } }}
              aria-label="Delete part"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={13}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#001f3f', mb: 2 }}>
                Detailed Information
              </Typography>
              <Grid container spacing={2}>
                {resolvedPartPhotos.length > 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{
                      p: 2,
                      backgroundColor: 'white',
                      border: '2px solid #2e7d32',
                      borderRadius: 2
                    }}>
                      <Typography variant="subtitle2" sx={{
                        fontWeight: 'bold',
                        mb: 2,
                        color: '#2e7d32',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Inventory2Icon /> Part Photos ({resolvedPartPhotos.length})
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        gap: 1.25,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        backgroundColor: '#f5f5f5',
                        borderRadius: 2,
                        p: 1.5,
                        minHeight: 112
                      }}>
                        {resolvedPartPhotos.map((photo, index) => (
                          <Box
                            key={`${photo.id || photo.url}-${index}`}
                            component="img"
                            src={photo.url}
                            alt={`Part ${index + 1}`}
                            onClick={() => openPartPhotoDialog(index)}
                            sx={{
                              width: { xs: 92, sm: 112 },
                              height: { xs: 76, sm: 90 },
                              objectFit: 'contain',
                              backgroundColor: 'white',
                              borderRadius: 1.5,
                              border: '1px solid #d7e3f4',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                              cursor: 'zoom-in'
                            }}
                            onError={(event) => {
                              event.currentTarget.style.display = 'none';
                            }}
                          />
                        ))}
                        </Box>
                    </Paper>
                  </Grid>
                )}
                {part.invoice_url ? (
                  <Grid item xs={12}>
                    <Paper sx={{
                      p: 2,
                      mt: 1,
                      backgroundColor: 'white',
                      border: '2px solid #1976d2',
                      borderRadius: 2
                    }}>
                      <InvoiceAttachmentList value={part.invoice_url} title="Invoice Attachments" />
                    </Paper>
                  </Grid>
                ) : null}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: '#f0f4ff' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 'bold', mb: 2, color: '#1a237e' }}
                    >
                      Inventory Transfer History
                    </Typography>

                    {loadingHistory ? (
                      <Typography variant="body2">Loading history...</Typography>
                    ) : movements.length === 0 ? (
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        No movement history available
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {movements.map((m, index) => (
                          <Box
                            key={m.id}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              backgroundColor: '#fff',
                              border: '1px solid #d1d9ff'
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {(m.movement_type || 'movement').toString().toUpperCase()}
                            </Typography>

                            <Typography variant="caption" color="textSecondary">
                              {m.from_location || getTruckName(m.from_truck_id, 'Shelf')} → {m.to_location || getTruckName(m.to_truck_id)}
                            </Typography>

                            <Typography variant="caption" display="block">
                              {formatPortalDate(m.created_at, { includeTime: true })}
                            </Typography>

                            {m.moved_by && (
                              <Typography variant="caption" display="block" color="textSecondary">
                                By: {m.moved_by}
                              </Typography>
                            )}

                            {m.notes && (
                              <Typography
                                variant="body2"
                                sx={{ mt: 0.5, fontStyle: 'italic' }}
                              >
                                {m.notes}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#fafafa', height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                      Part Specifications
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">OEM Part Number</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{part.part_number_oem}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Serial Number</Typography>
                        <Typography variant="body2">{part.serial_number || 'N/A'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Year</Typography>
                        <Typography variant="body2">{part.year}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Cross Reference</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{part.cross_reference || 'N/A'}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#fafafa', height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                      Storage Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Storage Condition</Typography>
                        <Typography variant="body2">{part.condition_of_storage}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Recommended Storage</Typography>
                        <Typography variant="body2">{part.recommended_storage}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#fafafa', height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                      Financial & Purchase Info
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Purchase Price</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                          {part.purchase_price ? `$${parseFloat(part.purchase_price).toFixed(2)}` : '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Date Purchased</Typography>
                        <Typography variant="body2">{part.date_purchased ? formatPortalDate(part.date_purchased) : '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Receipt Number</Typography>
                        <Typography variant="body2">{part.receipt_number}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: '#f8fbff', border: '1px solid #bfdbfe', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#0d47a1' }}>
                      Part Notes
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: part.notes ? 'text.primary' : 'text.secondary' }}>
                      {part.notes || 'No part notes added.'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: '#fff3e0' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#e65100' }}>
                      Condition Notes
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap', color: part.condition_notes ? 'text.primary' : 'text.secondary' }}>
                      {part.condition_notes || 'No condition notes added.'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <Dialog
        open={partPhotoDialogOpen}
        onClose={() => setPartPhotoDialogOpen(false)}
        fullScreen={partPhotoDialogFullScreen}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: partPhotoDialogFullScreen ? 0 : 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Inventory2Icon />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {part.sku} — Photo {partPhotoDialogIndex + 1} of {Math.max(1, resolvedPartPhotos.length)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedPartPhotoUrl && (
              <IconButton color="inherit" onClick={() => window.open(selectedPartPhotoUrl, '_blank')} size="small">
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton color="inherit" onClick={() => setPartPhotoZoom((z) => Math.min(4, Number((z + 0.25).toFixed(2))))} size="small" aria-label="Zoom in">
              <ZoomInIcon fontSize="small" />
            </IconButton>
            <IconButton color="inherit" onClick={() => setPartPhotoZoom((z) => Math.max(1, Number((z - 0.25).toFixed(2))))} size="small" aria-label="Zoom out">
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <IconButton color="inherit" onClick={() => setPartPhotoZoom(1)} size="small" aria-label="Reset zoom">
              <RestartAltIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => setPartPhotoDialogOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: '#0b1b2a' }}>
          <Box sx={{
            width: '100%',
            height: partPhotoDialogFullScreen ? 'calc(100vh - 64px)' : '70vh',
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}>
            {selectedPartPhotoUrl ? (
              <Box
                component="img"
                src={selectedPartPhotoUrl}
                alt="Part full"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  transform: `scale(${partPhotoZoom})`,
                  transformOrigin: 'center center',
                  transition: 'transform 120ms ease-out',
                  cursor: partPhotoZoom > 1 ? 'move' : 'zoom-in'
                }}
                onClick={() => setPartPhotoZoom((z) => (z === 1 ? 1.5 : 1))}
              />
            ) : (
              <Box sx={{ p: 3, color: 'white' }}>
                <Typography>No photo available.</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

    </>
  );
}

function VehiclePartCard({ part, allTrucks, onAssign, onEdit, onDelete, getTruckName, getConditionColor, getConditionLabel }) {
  const theme = useTheme();
  const partPhotoDialogFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(false);
  const [assignTo, setAssignTo] = useState(part.unit_intended_for || '');
  const [partPhotos, setPartPhotos] = useState([]);
  const [partPhotoDialogOpen, setPartPhotoDialogOpen] = useState(false);
  const [partPhotoDialogIndex, setPartPhotoDialogIndex] = useState(0);
  const [partPhotoZoom, setPartPhotoZoom] = useState(1);

  const resolvedPartPhotos = useMemo(() => {
    const gallery = (Array.isArray(partPhotos) ? partPhotos : []).filter((photo) => photo?.url);
    const cover = part.photo_url ? { id: 'cover', url: part.photo_url, original_name: 'Cover photo' } : null;
    if (!cover) return gallery;
    return gallery.some((photo) => photo.url === cover.url) ? gallery : [cover, ...gallery];
  }, [part.photo_url, partPhotos]);

  const selectedPartPhotoUrl = resolvedPartPhotos[partPhotoDialogIndex]?.url || null;

  useEffect(() => {
    setAssignTo(part.unit_intended_for || '');
  }, [part.unit_intended_for]);

  const fetchPartPhotos = useCallback(async () => {
    try {
      const res = await partService.getPhotos(part.id);
      setPartPhotos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching part photos:', err);
      setPartPhotos([]);
    }
  }, [part.id]);

  useEffect(() => {
    if (!expanded) return undefined;
    const refreshOpenCard = () => fetchPartPhotos();

    refreshOpenCard();
    window.addEventListener('partsUpdated', refreshOpenCard);
    window.addEventListener('dataUpdated', refreshOpenCard);
    return () => {
      window.removeEventListener('partsUpdated', refreshOpenCard);
      window.removeEventListener('dataUpdated', refreshOpenCard);
    };
  }, [expanded, fetchPartPhotos]);

  const handleAssign = async (newTruckId) => {
    const nextTruckId = newTruckId === '' ? null : parseInt(String(newTruckId), 10);
    setAssignTo(nextTruckId || '');
    await onAssign(part.id, nextTruckId);
  };

  const openPartPhotoDialog = (index = 0) => {
    setPartPhotoDialogIndex(index);
    setPartPhotoZoom(1);
    setPartPhotoDialogOpen(true);
  };

  return (
    <>
      <Paper sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid #d8e2ef',
        boxShadow: '0 10px 28px rgba(0,31,63,0.08)',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase' }}>
              {part.sku || 'No SKU'}
            </Typography>
            <Typography variant="h6" sx={{ color: '#001f3f', fontWeight: 'bold', lineHeight: 1.2, wordBreak: 'break-word' }}>
              {part.part_type || 'Unnamed Part'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              {[part.make, part.model, part.year].filter(Boolean).join(' ') || 'No vehicle spec'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <IconButton
              onClick={() => onEdit?.(part)}
              sx={{ color: '#1976d2', backgroundColor: '#e3f2fd', '&:hover': { backgroundColor: '#bbdefb' } }}
              aria-label="Edit part"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={() => onDelete?.(part)}
              sx={{ color: '#d32f2f', backgroundColor: '#ffebee', '&:hover': { backgroundColor: '#ffcdd2' } }}
              aria-label="Delete part"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip label={`Qty ${part.quantity ?? 0}`} sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold' }} />
          <Chip
            label={`${part.condition_scale}/5 ${getConditionLabel(part.condition_scale)}`}
            sx={{ backgroundColor: getConditionColor(part.condition_scale), color: 'white', fontWeight: 'bold' }}
          />
          <Chip
            label={part.used_new ? 'New' : 'Used'}
            sx={{
              backgroundColor: part.used_new ? '#c8e6c9' : '#ffccbc',
              color: part.used_new ? '#2e7d32' : '#d84315',
              fontWeight: 'bold'
            }}
          />
          <Chip
            label={part.current_value ? `$${parseFloat(part.current_value).toFixed(2)}` : 'No value'}
            sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold' }}
          />
        </Box>

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>Location</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#001f3f' }}>{part.location || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>Came Off From</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#001f3f' }}>{getTruckName(part.unit_came_off_from, 'Shelf')}</Typography>
          </Grid>
        </Grid>

        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold', mb: 0.75 }}>
            Assign To Vehicle
          </Typography>
          <Select value={assignTo} onChange={(event) => handleAssign(event.target.value)} displayEmpty>
            <MenuItem value="">
              <em>Not Assigned</em>
            </MenuItem>
            {allTrucks.map((vehicle) => (
              <MenuItem key={vehicle.id} value={vehicle.id}>
                {vehicle.name || `${vehicle.make} ${vehicle.model}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          fullWidth
          variant="outlined"
          onClick={() => setExpanded((current) => !current)}
          endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          sx={{ borderRadius: 2, fontWeight: 'bold' }}
        >
          {expanded ? 'Hide Details' : 'Show Details'}
        </Button>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Grid container spacing={1.5} sx={{ mt: 1 }}>
            {resolvedPartPhotos.length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 1.5, backgroundColor: '#f0fff4', borderRadius: 2, border: '1px solid #a5d6a7' }}>
                  <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                    <Inventory2Icon fontSize="small" /> Part Photos ({resolvedPartPhotos.length})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {resolvedPartPhotos.map((photo, index) => (
                      <Box
                        key={`${photo.id || photo.url}-${index}`}
                        component="img"
                        src={photo.url}
                        alt={`Part ${index + 1}`}
                        onClick={() => openPartPhotoDialog(index)}
                        sx={{
                          width: 88,
                          height: 72,
                          objectFit: 'contain',
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          border: '1px solid #d7e3f4',
                          boxShadow: '0 3px 10px rgba(0,0,0,0.10)',
                          cursor: 'zoom-in'
                        }}
                        onError={(event) => {
                          event.currentTarget.style.display = 'none';
                        }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 1.5, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>OEM Part Number</Typography>
                <Typography variant="body2">{part.part_number_oem || 'N/A'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 1.5, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>Serial Number</Typography>
                <Typography variant="body2">{part.serial_number || 'N/A'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 1.5, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>Storage</Typography>
                <Typography variant="body2">{part.condition_of_storage || part.recommended_storage || 'N/A'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 1.5, backgroundColor: '#f8fbff', borderRadius: 2, border: '1px solid #bfdbfe' }}>
                <Typography variant="caption" sx={{ color: '#0d47a1', fontWeight: 'bold' }}>Part Notes</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{part.notes || 'No part notes added.'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 1.5, backgroundColor: '#fff8e1', borderRadius: 2 }}>
                <Typography variant="caption" sx={{ color: '#8d6e00', fontWeight: 'bold' }}>Condition Notes</Typography>
                <Typography variant="body2">{part.condition_notes || 'No notes added.'}</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>
      <Dialog
        open={partPhotoDialogOpen}
        onClose={() => setPartPhotoDialogOpen(false)}
        fullScreen={partPhotoDialogFullScreen}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: partPhotoDialogFullScreen ? 0 : 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Inventory2Icon />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {part.sku} — Photo {partPhotoDialogIndex + 1} of {Math.max(1, resolvedPartPhotos.length)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedPartPhotoUrl && (
              <IconButton color="inherit" onClick={() => window.open(selectedPartPhotoUrl, '_blank')} size="small">
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton color="inherit" onClick={() => setPartPhotoZoom((z) => Math.min(4, Number((z + 0.25).toFixed(2))))} size="small" aria-label="Zoom in">
              <ZoomInIcon fontSize="small" />
            </IconButton>
            <IconButton color="inherit" onClick={() => setPartPhotoZoom((z) => Math.max(1, Number((z - 0.25).toFixed(2))))} size="small" aria-label="Zoom out">
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <IconButton color="inherit" onClick={() => setPartPhotoZoom(1)} size="small" aria-label="Reset zoom">
              <RestartAltIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => setPartPhotoDialogOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: '#0b1b2a' }}>
          <Box sx={{
            width: '100%',
            height: partPhotoDialogFullScreen ? 'calc(100vh - 64px)' : '70vh',
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}>
            {selectedPartPhotoUrl ? (
              <Box
                component="img"
                src={selectedPartPhotoUrl}
                alt="Part full"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  transform: `scale(${partPhotoZoom})`,
                  transformOrigin: 'center center',
                  transition: 'transform 120ms ease-out',
                  cursor: partPhotoZoom > 1 ? 'move' : 'zoom-in'
                }}
                onClick={() => setPartPhotoZoom((z) => (z === 1 ? 1.5 : 1))}
              />
            ) : (
              <Box sx={{ p: 3, color: 'white' }}>
                <Typography>No photo available.</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function TruckDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const photoDialogFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const partsCardView = useMediaQuery(theme.breakpoints.down('md'));
  const requestedTab = searchParams.get('tab');
  const [mainTab, setMainTab] = useState(VALID_MAIN_TABS.includes(requestedTab) ? requestedTab : 'overview');
  const [truck, setTruck] = useState(null);
  const [truckPhotos, setTruckPhotos] = useState([]);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [photoDialogIndex, setPhotoDialogIndex] = useState(0);
  const [parts, setParts] = useState([]);
  const [allTrucks, setAllTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCondition, setFilterCondition] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [orderBy, setOrderBy] = useState('part_type');
  const [order, setOrder] = useState('asc');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addPartDialogOpen, setAddPartDialogOpen] = useState(false);
  const [editPartDialogOpen, setEditPartDialogOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [deletePart, setDeletePart] = useState(null);
  const [deletingPart, setDeletingPart] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoZoom, setPhotoZoom] = useState(1);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [idiosyncrasyItems, setIdiosyncrasyItems] = useState([]);
  const [idiosyncrasyDraft, setIdiosyncrasyDraft] = useState('');
  const [idiosyncrasySaving, setIdiosyncrasySaving] = useState(false);
  const [idiosyncrasiesDialogOpen, setIdiosyncrasiesDialogOpen] = useState(false);
  const idiosyncrasyHydratedRef = useRef(false);
  const lastSavedIdiosyncrasiesRef = useRef('');
  const [importantNotes, setImportantNotes] = useState('');
  const [importantNotesSaving, setImportantNotesSaving] = useState(false);
  const [importantNotesDialogOpen, setImportantNotesDialogOpen] = useState(false);
  const importantNotesHydratedRef = useRef(false);
  const lastSavedImportantNotesRef = useRef('');
  const [prefsTick, setPrefsTick] = useState(0);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (VALID_MAIN_TABS.includes(requestedTab)) setMainTab(requestedTab);
  }, [requestedTab]);

  const sortedNavigationTrucks = useMemo(() => {
    return [...(allTrucks || [])].sort((a, b) => {
      const av = Number(a?.display_order || 0);
      const bv = Number(b?.display_order || 0);
      if (av !== bv) return av - bv;
      return Number(a?.id || 0) - Number(b?.id || 0);
    });
  }, [allTrucks]);

  const currentNavigationIndex = sortedNavigationTrucks.findIndex((item) => String(item.id) === String(id));
  const previousTruck = currentNavigationIndex > 0 ? sortedNavigationTrucks[currentNavigationIndex - 1] : null;
  const nextTruck = currentNavigationIndex >= 0 && currentNavigationIndex < sortedNavigationTrucks.length - 1
    ? sortedNavigationTrucks[currentNavigationIndex + 1]
    : null;

  const rememberCurrentTruckForList = useCallback(() => {
    try {
      sessionStorage.setItem(LAST_VIEWED_TRUCK_KEY, String(id));
    } catch {
      // ignore
    }
  }, [id]);

  const handleBackToVehicles = useCallback(() => {
    rememberCurrentTruckForList();
    let returnPath = '/trucks';
    try {
      returnPath = sessionStorage.getItem(TRUCK_RETURN_PATH_KEY) || '/trucks';
    } catch {
      returnPath = '/trucks';
    }
    navigate(returnPath);
  }, [navigate, rememberCurrentTruckForList]);

  const handleNavigateTruck = useCallback((truckId) => {
    if (!truckId) return;
    rememberCurrentTruckForList();
    navigate(`/truck/${truckId}`);
  }, [navigate, rememberCurrentTruckForList]);

  const jumpToOverviewSection = (sectionId) => {
    setMainTab('overview');
    window.setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const overviewJumpSections = [
    { label: 'Photos', id: 'truck-section-photos' },
    { label: 'Basic', id: 'truck-section-basic' },
    { label: 'Specs', id: 'truck-section-specs' },
    { label: 'Fuel', id: 'truck-section-fuel' },
    { label: 'Lighting', id: 'truck-section-lighting' },
    { label: 'Condition', id: 'truck-section-condition' },
    { label: 'Parts', id: 'truck-section-parts' },
  ];

  const openPhotoDialog = () => {
    setPhotoZoom(1);
    setPhotoDialogIndex(activePhotoIndex);
    setPhotoDialogOpen(true);
  };

  const handleTruckUpdated = async (updatedTruck) => {
    const truckId = updatedTruck?.id || truck?.id || id;

    try {
      const truckRes = await api.get(`/trucks/${truckId}`);
      setTruck(truckRes.data);
      setAllTrucks((prev) =>
        prev.map((t) => (String(t.id) === String(truckId) ? truckRes.data : t))
      );
    } catch (err) {
      console.error('Failed to refresh truck after edit:', err);
      setTruck((prev) => ({ ...prev, ...updatedTruck }));
    }

    window.dispatchEvent(new Event('vehiclesUpdated'));
    window.dispatchEvent(new Event('dataUpdated'));
    setSnackbar({ open: true, message: 'Truck updated successfully!', severity: 'success' });
  };

  const handleVehiclePartAdded = async (newPart) => {
    setParts((prev) => [...prev, newPart]);
    await loadData({ silent: true });
    window.dispatchEvent(new Event('partsUpdated'));
    window.dispatchEvent(new Event('dataUpdated'));
    setSnackbar({ open: true, message: 'Part added to vehicle successfully.', severity: 'success' });
  };

  const handleVehiclePartUpdated = async (updatedPart) => {
    setParts((prev) => {
      const belongsToVehicle = String(updatedPart.unit_came_off_from) === String(id) || String(updatedPart.unit_intended_for) === String(id);
      const exists = prev.some((part) => part.id === updatedPart.id);
      if (!belongsToVehicle) return prev.filter((part) => part.id !== updatedPart.id);
      if (exists) return prev.map((part) => (part.id === updatedPart.id ? updatedPart : part));
      return [...prev, updatedPart];
    });
    await loadData({ silent: true });
    window.dispatchEvent(new Event('partsUpdated'));
    window.dispatchEvent(new Event('dataUpdated'));
    setSnackbar({ open: true, message: 'Part updated successfully.', severity: 'success' });
  };

  const handleDeleteVehiclePart = async () => {
    if (!deletePart?.id) return;
    try {
      setDeletingPart(true);
      await partService.delete(deletePart.id);
      setParts((prev) => prev.filter((part) => part.id !== deletePart.id));
      await loadData({ silent: true });
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      setSnackbar({ open: true, message: 'Part deleted successfully.', severity: 'success' });
      setDeletePart(null);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.error || err?.message || 'Failed to delete part.',
      });
    } finally {
      setDeletingPart(false);
    }
  };

  const handleInspectionCreated = async (inspection) => {
    const nextOdometer = inspection?.odometer;
    if (nextOdometer !== null && nextOdometer !== undefined && nextOdometer !== '') {
      setTruck((prev) => (prev ? { ...prev, odometer: nextOdometer, updated_at: new Date().toISOString() } : prev));
      setAllTrucks((prev) => prev.map((t) => (String(t.id) === String(id) ? { ...t, odometer: nextOdometer } : t)));
    }

    window.dispatchEvent(new Event('vehiclesUpdated'));
    window.dispatchEvent(new Event('dataUpdated'));

    try {
      const truckRes = await api.get(`/trucks/${id}`);
      setTruck(truckRes.data);
      setAllTrucks((prev) => prev.map((t) => (String(t.id) === String(id) ? truckRes.data : t)));
    } catch (err) {
      console.error('Failed to refresh vehicle after inspection:', err);
    }
  };

  useEffect(() => {
    const handler = () => setPrefsTick((v) => v + 1);
    window.addEventListener('prefsUpdated', handler);
    return () => window.removeEventListener('prefsUpdated', handler);
  }, []);

  useEffect(() => {
    rememberCurrentTruckForList();
  }, [rememberCurrentTruckForList]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const handler = () => loadData({ silent: true });
    window.addEventListener('dataUpdated', handler);
    window.addEventListener('partsUpdated', handler);
    window.addEventListener('vehiclesUpdated', handler);
    return () => {
      window.removeEventListener('dataUpdated', handler);
      window.removeEventListener('partsUpdated', handler);
      window.removeEventListener('vehiclesUpdated', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function loadData({ silent = false } = {}) {
    try {
      if (!silent) setLoading(true);
      const [truckRes, trucksRes, partsRes, photosRes] = await Promise.all([
        api.get(`/trucks/${id}`),
        api.get('/trucks?includeArchived=1'),
        api.get(`/trucks/${id}/parts`),
        api.get(`/trucks/${id}/photos`).catch(() => ({ data: [] })),
      ]);

      setTruck(truckRes.data);
      setAllTrucks(trucksRes.data || []);
      setParts(partsRes.data || []);
      setTruckPhotos(photosRes.data || []);
      setActivePhotoIndex(0);

      const rawIdios = truckRes?.data?.idiosyncrasies || '';
      setIdiosyncrasyItems(parseChecklist(rawIdios));
      lastSavedIdiosyncrasiesRef.current = rawIdios || '';
      idiosyncrasyHydratedRef.current = true;
      const rawImportantNotes = truckRes?.data?.important_notes || '';
      setImportantNotes(rawImportantNotes);
      lastSavedImportantNotesRef.current = rawImportantNotes;
      importantNotesHydratedRef.current = true;
    } catch (err) {
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  }

  const handleUploadTruckPhotos = async (files) => {
    if (!truck?.id) return;
    const list = Array.from(files || []);
    if (!list.length) return;

    try {
      setPhotoUploading(true);
      await truckService.uploadPhotos(truck.id, list);
      const [truckRes, photosRes] = await Promise.all([
        api.get(`/trucks/${truck.id}`),
        api.get(`/trucks/${truck.id}/photos`).catch(() => ({ data: [] })),
      ]);
      setTruck(truckRes.data);
      setTruckPhotos(photosRes.data || []);
      setActivePhotoIndex(0);
      setSnackbar({ open: true, severity: 'success', message: 'Photos uploaded.' });
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.error || err?.message || 'Failed to upload photos.';
      setSnackbar({ open: true, severity: 'error', message });
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDeleteActivePhoto = async (photo) => {
    if (!truck?.id) return;
    if (!photo) return;
    try {
      await truckService.deletePhoto(truck.id, photo.id);
      const [truckRes, photosRes] = await Promise.all([
        api.get(`/trucks/${truck.id}`),
        api.get(`/trucks/${truck.id}/photos`).catch(() => ({ data: [] })),
      ]);
      setTruck(truckRes.data);
      setTruckPhotos(photosRes.data || []);
      setActivePhotoIndex(0);
      setPhotoDialogIndex(0);
      setSnackbar({ open: true, severity: 'success', message: 'Photo deleted.' });
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.error || err?.message || 'Failed to delete photo.';
      setSnackbar({ open: true, severity: 'error', message });
    }
  };

  const persistIdiosyncrasies = async (items) => {
    if (!truck?.id) return;
    const next = serializeChecklist(items);
    if (next === (lastSavedIdiosyncrasiesRef.current || '')) return;

    try {
      setIdiosyncrasySaving(true);
      await truckService.update(truck.id, { idiosyncrasies: next || null });
      lastSavedIdiosyncrasiesRef.current = next;
      setTruck((prev) => (prev ? { ...prev, idiosyncrasies: next } : prev));
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.error || err?.message || 'Failed to save idiosyncrasies.';
      setSnackbar({ open: true, severity: 'error', message });
    } finally {
      setIdiosyncrasySaving(false);
    }
  };

  useEffect(() => {
    if (!truck?.id) return undefined;
    if (!idiosyncrasyHydratedRef.current) return undefined;
    const next = serializeChecklist(idiosyncrasyItems);
    if (next === (lastSavedIdiosyncrasiesRef.current || '')) return undefined;
    const t = setTimeout(() => { persistIdiosyncrasies(idiosyncrasyItems); }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truck?.id, idiosyncrasyItems]);

  const persistImportantNotes = async () => {
    if (!truck?.id) return;
    if ((importantNotes || '') === (lastSavedImportantNotesRef.current || '')) return;

    try {
      setImportantNotesSaving(true);
      await truckService.update(truck.id, { important_notes: importantNotes || null });
      lastSavedImportantNotesRef.current = importantNotes || '';
      setTruck((prev) => (prev ? { ...prev, important_notes: importantNotes || null } : prev));
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.error || err?.message || 'Failed to save notes.';
      setSnackbar({ open: true, severity: 'error', message });
    } finally {
      setImportantNotesSaving(false);
    }
  };

  useEffect(() => {
    if (!truck?.id) return undefined;
    if (!importantNotesHydratedRef.current) return undefined;
    if ((importantNotes || '') === (lastSavedImportantNotesRef.current || '')) return undefined;
    const t = setTimeout(() => { persistImportantNotes(); }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truck?.id, importantNotes]);

  async function handleAssignPart(partId, newTruckId) {
    try {
      const target = parts.find((p) => p.id === partId);
      const payload = {
        unit_came_off_from: target?.unit_came_off_from ?? null,
        unit_intended_for: newTruckId ?? null,
      };

      const response = await api.put(`/parts/${partId}`, payload);
      const updatedPart = response.data;

      setParts((prev) =>
        prev.map((p) => (p.id === partId ? { ...p, ...updatedPart } : p))
      );
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      setSnackbar({ open: true, message: 'Part assigned successfully!', severity: 'success' });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to assign part', severity: 'error' });
    }
  }

  function getTruckName(truckId, fallback = 'Unassigned') {
    if (!truckId) return fallback;
    const t = allTrucks.find(truck => truck.id === truckId);
    return t ? (t.name || `${t.make} ${t.model}`) : `Truck #${truckId}`;
  }

  function getConditionColor(scale) {
    if (scale >= 5) return '#4caf50';
    if (scale >= 4) return '#8bc34a';
    if (scale >= 3) return '#ff9800';
    if (scale >= 2) return '#ff5722';
    return '#f44336';
  }

  function getConditionLabel(scale) {
    if (scale >= 5) return 'Excellent';
    if (scale >= 4) return 'Very Good';
    if (scale >= 3) return 'Good';
    if (scale >= 2) return 'Fair';
    return 'Poor';
  }

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredParts = parts
    .filter(part => {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        part.part_type?.toLowerCase().includes(query) ||
        part.sku?.toLowerCase().includes(query) ||
        part.location?.toLowerCase().includes(query) ||
        part.part_number_oem?.toLowerCase().includes(query) ||
        part.serial_number?.toLowerCase().includes(query);

      const matchesCondition = filterCondition === 'all' ||
        (filterCondition === 'excellent' && part.condition_scale >= 5) ||
        (filterCondition === 'good' && part.condition_scale >= 3 && part.condition_scale < 5) ||
        (filterCondition === 'fair' && part.condition_scale < 3);

      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'new' && part.used_new === true) ||
        (filterStatus === 'used' && part.used_new === false);

      return matchesSearch && matchesCondition && matchesStatus;
    })
    .sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      if (orderBy === 'current_value' || orderBy === 'condition_scale' || orderBy === 'quantity') {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      }

      if (order === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!truck) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" color="error">Truck not found</Typography>
      </Box>
    );
  }

  const showField = () => true;

  const favorite = truck?.id ? isFavoriteTruck(truck.id) : false;
  const resolvedPhotos = (() => {
    const list = (Array.isArray(truckPhotos) ? truckPhotos : [])
      .map((photo) => {
        const url = normalizeFileUrl(photo?.url || photo?.photo_url || photo?.path);
        return url ? { ...photo, url } : null;
      })
      .filter(Boolean);
    const coverUrl = normalizeFileUrl(truck?.photo_url);
    if (coverUrl && !list.some((p) => p?.url === coverUrl)) {
      list.unshift({ id: 'cover', url: coverUrl, original_name: 'Cover photo' });
    }
    return list;
  })();
  const displayPhotoUrl = resolvedPhotos[activePhotoIndex]?.url || null;
  const dialogPhotoUrl = resolvedPhotos[photoDialogIndex]?.url || null;
  const activePhoto = resolvedPhotos[activePhotoIndex] || null;
  const dialogPhoto = resolvedPhotos[photoDialogIndex] || null;
  const canDeleteActivePhoto = Boolean(truck?.id && activePhoto);
  const canDeleteDialogPhoto = Boolean(truck?.id && dialogPhoto);

  const handleToggleFavorite = () => {
    if (!truck?.id) return;
    const wasFavorite = isFavoriteTruck(truck.id);
    toggleFavoriteTruck(truck.id);
    setPrefsTick((v) => v + 1);
    setSnackbar({ open: true, severity: 'success', message: wasFavorite ? 'Removed from favorites.' : 'Added to favorites.' });
  };

  const handleToggleArchive = async () => {
    if (!truck?.id) return;
    try {
      setArchiving(true);
      const endpoint = truck.archived ? 'unarchive' : 'archive';
      const res = await api.put(`/trucks/${truck.id}/${endpoint}`);
      setTruck(res.data);
      window.dispatchEvent(new Event('vehiclesUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      setSnackbar({
        open: true,
        severity: 'success',
        message: truck.archived ? 'Truck unarchived.' : 'Truck archived.'
      });
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.error || err?.message || 'Failed to update archive status.';
      setSnackbar({ open: true, severity: 'error', message });
    } finally {
      setArchiving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 2 }, minHeight: '100dvh' }}>
      <Card className="portal-page-header" sx={{
        mb: 1.25,
        background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
        color: 'white',
        borderRadius: 2.25,
        boxShadow: '0 6px 24px rgba(0, 31, 63, 0.26)'
      }}>
        <CardContent sx={{ p: { xs: 1.15, sm: 1.35, md: 1.55 }, '&:last-child': { pb: { xs: 1.15, sm: 1.35, md: 1.55 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.25 }, flexWrap: 'wrap' }}>
            <Avatar sx={{
              width: { xs: 40, sm: 46 },
              height: { xs: 40, sm: 46 },
              backgroundColor: '#00ff97',
              boxShadow: '0 3px 14px rgba(0, 255, 151, 0.35)'
            }}>
              <LocalShippingIcon sx={{ fontSize: { xs: 23, sm: 27 }, color: '#001f3f' }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 0.1, fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' }, lineHeight: 1.05 }}>
                {truck.name || `${truck.make} ${truck.model}`}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontSize: { xs: '0.68rem', sm: '0.76rem' }, lineHeight: 1.25 }}>
                {truck.make} {truck.model} • {truck.year}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToVehicles}
                sx={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #e0f2fe 100%)',
                  color: '#001f3f',
                  fontWeight: 900,
                  borderRadius: 999,
                  minHeight: 36,
                  px: 1.5,
                  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.18)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #e0f2fe 0%, #ffffff 100%)',
                    boxShadow: '0 10px 26px rgba(0, 255, 151, 0.24)',
                  },
                }}
              >
                Back to List
              </Button>
              <Button
                variant="outlined"
                startIcon={<NavigateBeforeIcon />}
                disabled={!previousTruck}
                onClick={() => handleNavigateTruck(previousTruck?.id)}
                sx={{
                  borderColor: 'rgba(255,255,255,0.65)',
                  color: 'white',
                  fontWeight: 800,
                  borderRadius: 999,
                  minHeight: 36,
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.38)', borderColor: 'rgba(255,255,255,0.22)' },
                  '&:hover': { borderColor: '#00ff97', backgroundColor: 'rgba(0, 255, 151, 0.1)' },
                }}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                endIcon={<NavigateNextIcon />}
                disabled={!nextTruck}
                onClick={() => handleNavigateTruck(nextTruck?.id)}
                sx={{
                  borderColor: 'rgba(255,255,255,0.65)',
                  color: 'white',
                  fontWeight: 800,
                  borderRadius: 999,
                  minHeight: 36,
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.38)', borderColor: 'rgba(255,255,255,0.22)' },
                  '&:hover': { borderColor: '#00ff97', backgroundColor: 'rgba(0, 255, 151, 0.1)' },
                }}
              >
                Next
              </Button>
              <Chip
                label={truck.status || 'Active'}
                sx={{
                  backgroundColor: '#00ff97',
                  color: '#001f3f',
                  fontWeight: 'bold',
                  fontSize: '0.8rem',
                  px: 1.25,
                  height: 36,
                }}
              />
              <IconButton
                key={`fav-${prefsTick}`}
                onClick={handleToggleFavorite}
                sx={{
                  color: favorite ? '#ffd54f' : 'rgba(255,255,255,0.85)',
                  border: '1px solid rgba(255,255,255,0.35)',
                  width: 36,
                  height: 36,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.10)' }
                }}
                aria-label="Toggle favorite"
              >
                {favorite ? <StarIcon /> : <StarBorderIcon />}
              </IconButton>
              <Button
                variant="outlined"
                startIcon={truck.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                onClick={() => setArchiveConfirmOpen(true)}
                sx={{
                  borderColor: 'rgba(255,255,255,0.7)',
                  color: 'white',
                  fontWeight: 'bold',
                  minHeight: 36,
                  '&:hover': {
                    borderColor: '#00ff97',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                {truck.archived ? 'Unarchive' : 'Archive'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditDialogOpen(true)}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  fontWeight: 'bold',
                  minHeight: 36,
                  '&:hover': {
                    borderColor: '#00ff97',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Edit Vehicle
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={mainTab}
          onChange={(_e, v) => setMainTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #e0e0e0',
            '& .MuiTab-root': { color: '#546e7a', fontWeight: 'bold' },
            '& .Mui-selected': { color: '#001f3f' },
            '& .MuiTabs-indicator': { backgroundColor: '#2e7d32', height: 3 },
          }}
        >
          <Tab value="overview" label="Overview" />
          <Tab value="service" label="Service" icon={<BuildIcon />} iconPosition="start" />
          <Tab value="consumables" label="Maintenance Reminders" icon={<NotificationsPausedIcon />} iconPosition="start" />
          <Tab value="gloveBox" label="Glove Box" icon={<ReceiptIcon />} iconPosition="start" />
          <Tab value="inspections" label="Inspections" icon={<WarningIcon />} iconPosition="start" />
          <Tab value="washes" label="Vehicle Washes" icon={<LocalCarWashIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {mainTab === 'service' ? (
        <TruckServiceLog truckId={truck?.id} />
      ) : mainTab === 'consumables' ? (
        <TruckConsumables truckId={truck?.id} truck={truck} />
      ) : mainTab === 'gloveBox' ? (
        <TruckGloveBox truckId={truck?.id} />
      ) : mainTab === 'inspections' ? (
        <TruckInspectionLogs truckId={truck?.id} onInspectionCreated={handleInspectionCreated} />
      ) : mainTab === 'washes' ? (
        <TruckWashes truckId={truck?.id} truck={truck} />
      ) : (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: 3,
              height: '100%',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <CardContent sx={{ p: { xs: 1.25, sm: 1.75 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                  <Avatar sx={{ width: 38, height: 38, bgcolor: '#001f3f', color: '#00ff97', boxShadow: '0 5px 14px rgba(0,31,63,0.18)' }}>
                    <DirectionsCarIcon sx={{ fontSize: 21 }} />
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: '#001f3f', lineHeight: 1.15 }}>
                      Vehicle Information
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Photos, core details, specifications, and vehicle condition
                    </Typography>
                  </Box>
                  <Divider sx={{ flex: 1, ml: 1, display: { xs: 'none', sm: 'block' } }} />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.75 }}>
                  {overviewJumpSections.map((section) => (
                    <Chip
                      key={section.id}
                      label={section.label}
                      size="small"
                      clickable
                      onClick={() => jumpToOverviewSection(section.id)}
                      sx={{
                        fontWeight: 800,
                        bgcolor: '#f4f8ff',
                        border: '1px solid #cfe0f7',
                        color: '#003d7a',
                        '&:hover': { bgcolor: '#e8f2ff' },
                      }}
                    />
                  ))}
                </Box>

                <Box sx={{ mb: 2.5, display: 'flex', gap: { xs: 2, md: 2.5 }, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
                  <Box sx={{ width: { xs: '100%', md: 320, lg: 340 }, flex: '0 0 auto' }}>
                    <Paper id="truck-section-photos" sx={{
                      p: { xs: 1.25, sm: 1.5 },
                      backgroundColor: '#ffffff',
                      borderRadius: 2,
                      border: '2px solid #e0e0e0',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
                        <Typography variant="subtitle1" sx={{
                          fontWeight: 'bold',
                          color: '#001f3f',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          <LocalShippingIcon /> Photos
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Box component="label" sx={{ cursor: photoUploading ? 'default' : 'pointer' }}>
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.gif,.webp"
                              multiple
                              disabled={photoUploading}
                              onChange={(e) => { handleUploadTruckPhotos(e.target.files); e.target.value = ''; }}
                              style={{ display: 'none' }}
                            />
                            <Button
                              variant="outlined"
                              component="span"
                              size="small"
                              disabled={photoUploading}
                              startIcon={photoUploading ? <CircularProgress size={18} /> : <EditIcon />}
                              sx={{ borderRadius: 2 }}
                            >
                              {photoUploading ? 'Uploading...' : 'Upload'}
                            </Button>
                          </Box>
                          {displayPhotoUrl && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={openPhotoDialog}
                              startIcon={<ZoomInIcon />}
                              sx={{ borderRadius: 2 }}
                            >
                              Zoom
                            </Button>
                          )}
                          {canDeleteActivePhoto && (
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteActivePhoto(activePhoto)}
                              aria-label="Delete selected photo"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Box>

                      {displayPhotoUrl ? (
                        <>
                          <Box
                            sx={{
                              width: '100%',
                              height: { xs: 150, sm: 170, lg: 190 },
                              borderRadius: 1.5,
                              border: '1px solid #e0e0e0',
                              backgroundColor: '#ffffff',
                              cursor: 'zoom-in',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              p: 1,
                              overflow: 'hidden',
                            }}
                            onClick={openPhotoDialog}
                          >
                            <Box
                              component="img"
                              src={displayPhotoUrl}
                              alt="Truck"
                              sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                              }}
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          </Box>
                          {resolvedPhotos.length > 1 && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                              {resolvedPhotos.slice(0, 12).map((p, idx) => (
                                <Box
                                  key={`${p.id}-${idx}`}
                                  component="img"
                                  src={p.url}
                                  alt={`Truck ${idx + 1}`}
                                  onClick={() => setActivePhotoIndex(idx)}
                                  sx={{
                                    width: 44,
                                    height: 34,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    border: idx === activePhotoIndex ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                    cursor: 'pointer'
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </>
                      ) : (
                        <Box sx={{
                          height: { xs: 150, sm: 170, lg: 190 },
                          p: 2,
                          backgroundColor: '#fafafa',
                          borderRadius: 1.5,
                          border: '1px dashed #cfd8dc',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                        }}>
                          <Typography variant="body2" color="textSecondary">
                            No photos uploaded yet.
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Box>

                  <Box sx={{ flex: '1 1 0', minWidth: 0, width: { xs: '100%', md: 'auto' }, maxWidth: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Paper id="truck-section-basic" sx={{
                      p: 2.5,
                      backgroundColor: '#e3f2fd',
                      borderRadius: 2,
                      border: '2px solid #1976d2',
                      width: '100%',
                      maxWidth: '100%',
                    }}>
                      <Typography variant="subtitle1" sx={{
                        fontWeight: 'bold',
                        color: '#1565c0',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <InfoIcon /> Basic Information
                      </Typography>
                      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12} sm={6} sx={{ display: showField(truck.name) ? 'block' : 'none' }}>
                          <Box sx={{
                            p: 1.5,
                            backgroundColor: 'white',
                            borderRadius: 1.5,
                            borderLeft: '4px solid #1976d2'
                          }}>
                            <Typography variant="caption" sx={{
                              color: '#666',
                              textTransform: 'uppercase',
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }}>
                              Name
                            </Typography>
                            <Typography variant="h6" sx={{ color: '#001f3f', fontWeight: 'bold' }}>
                              {truck.name || 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box sx={{
                            p: 1.5,
                            backgroundColor: 'white',
                            borderRadius: 1.5,
                            borderLeft: '4px solid #1976d2'
                          }}>
                            <Typography variant="caption" sx={{
                              color: '#666',
                              textTransform: 'uppercase',
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }}>
                              Make & Model
                            </Typography>
                            <Typography variant="h6" sx={{ color: '#001f3f', fontWeight: 'bold' }}>
                              {truck.make} {truck.model}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} sx={{ display: showField(truck.license_plate) ? 'block' : 'none' }}>
                          <Box sx={{
                            p: 1.5,
                            backgroundColor: 'white',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <InfoIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                            <Box>
                              <Typography variant="caption" sx={{
                                color: '#666',
                                textTransform: 'uppercase',
                                fontWeight: 'bold',
                                fontSize: '0.65rem'
                              }}>
                                License Plate
                              </Typography>
                              <Typography variant="body1" sx={{ color: '#001f3f', fontWeight: 'bold' }}>
                                {truck.license_plate || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} sx={{ display: showField(truck.year) ? 'block' : 'none' }}>
                          <Box sx={{
                            p: 1.5,
                            backgroundColor: 'white',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <CalendarTodayIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                            <Box>
                              <Typography variant="caption" sx={{
                                color: '#666',
                                textTransform: 'uppercase',
                                fontWeight: 'bold',
                                fontSize: '0.65rem'
                              }}>
                                Year
                              </Typography>
                              <Typography variant="body1" sx={{ color: '#001f3f', fontWeight: 'bold' }}>
                                {truck.year ?? 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} sx={{ display: showField(truck.odometer) ? 'block' : 'none' }}>
                          <Box sx={{
                            p: 1.5,
                            backgroundColor: 'white',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <SpeedIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                            <Box>
                              <Typography variant="caption" sx={{
                                color: '#666',
                                textTransform: 'uppercase',
                                fontWeight: 'bold',
                                fontSize: '0.65rem'
                              }}>
                                Odometer
                              </Typography>
                              <Typography variant="body1" sx={{ color: '#001f3f', fontWeight: 'bold' }}>
                                {formatMaybeNumber(truck.odometer)}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} sx={{ display: showField(truck.serial_number) ? 'block' : 'none' }}>
                          <Box sx={{
                            p: 1.5,
                            backgroundColor: 'white',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <ConfirmationNumberIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                            <Box>
                              <Typography variant="caption" sx={{
                                color: '#666',
                                textTransform: 'uppercase',
                                fontWeight: 'bold',
                                fontSize: '0.65rem'
                              }}>
                                Serial Number
                              </Typography>
                              <Typography variant="body1" sx={{ color: '#001f3f', fontWeight: 'bold' }}>
                                {truck.serial_number || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} sx={{ display: showField(truck.estimated_value) ? 'block' : 'none' }}>
                          <Box sx={{
                            p: 1.5,
                            backgroundColor: 'white',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <AttachMoneyIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                            <Box>
                              <Typography variant="caption" sx={{
                                color: '#666',
                                textTransform: 'uppercase',
                                fontWeight: 'bold',
                                fontSize: '0.65rem'
                              }}>
                                Estimated Value
                              </Typography>
                              <Typography variant="body1" sx={{ color: '#001f3f', fontWeight: 'bold' }}>
                                {truck.estimated_value == null || truck.estimated_value === '' ? 'N/A' : `$${formatMaybeNumber(truck.estimated_value)}`}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                      <Box sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid rgba(25,118,210,0.20)',
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 1.5,
                      }}>
                        <Button
                          fullWidth
                          onClick={() => setImportantNotesDialogOpen(true)}
                          sx={{
                            p: 1.5,
                            minHeight: 88,
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            backgroundColor: 'white',
                            borderRadius: 1.5,
                            border: '1px solid #d8e2ef',
                            borderLeft: '4px solid #001f3f',
                            color: '#001f3f',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#f8fafc' }
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', fontWeight: 'bold', fontSize: '0.65rem' }}>
                              Notes
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                              {importantNotes ? 'View / Edit Notes' : 'Add Notes'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {importantNotes || 'Write vehicle overview notes'}
                            </Typography>
                          </Box>
                        </Button>
                        <Button
                          fullWidth
                          onClick={() => setIdiosyncrasiesDialogOpen(true)}
                          sx={{
                            p: 1.5,
                            minHeight: 88,
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            backgroundColor: 'white',
                            borderRadius: 1.5,
                            border: '1px solid #d8e2ef',
                            borderLeft: '4px solid #6a1b9a',
                            color: '#001f3f',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#f8fafc' }
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', fontWeight: 'bold', fontSize: '0.65rem' }}>
                              Idiosyncrasies
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                              {idiosyncrasyItems.length} item{idiosyncrasyItems.length === 1 ? '' : 's'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Open checklist popup
                            </Typography>
                          </Box>
                        </Button>
                      </Box>
                    </Paper>
                    </Box>
                  </Box>
                </Box>

                <Accordion id="truck-section-specs" disableGutters sx={{
                  mb: 2.5,
                  backgroundColor: '#fff3e0',
                  borderRadius: 2,
                  border: '2px solid #ff9800',
                  overflow: 'hidden',
                  '&:before': { display: 'none' }
                }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#e65100' }} />} sx={{ px: 2.5, py: 1, color: '#e65100' }}>
                    <Typography variant="subtitle1" sx={{
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <SettingsIcon /> Mechanical Specifications
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.front_axle) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #ff9800'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Front Axle
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.front_axle || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.rear_door_type || truck.tailgate) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #ff9800'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Rear Door Type
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.rear_door_type || truck.tailgate || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.transmission) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #ff9800'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Transmission
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.transmission || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.clutch) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #ff9800'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Clutch
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.clutch || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sx={{ display: showField(truck.box) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #ff9800'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Box
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.box || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.seats) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          borderLeft: '3px solid #ff9800'
                        }}>
                          <EventSeatIcon sx={{ color: '#ff9800', fontSize: 22 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{
                              color: '#999',
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                              fontWeight: 'bold'
                            }}>
                              Seats
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                              {truck.seats || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.battery) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          borderLeft: '3px solid #ff9800'
                        }}>
                          <BatteryChargingFullIcon sx={{ color: '#ff9800', fontSize: 22 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{
                              color: '#999',
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                              fontWeight: 'bold'
                            }}>
                              Battery System Type
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                              {truck.battery || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.tires) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          borderLeft: '3px solid #ff9800'
                        }}>
                          <TireRepairIcon sx={{ color: '#ff9800', fontSize: 22 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{
                              color: '#999',
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                              fontWeight: 'bold'
                            }}>
                              Tires
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                              {truck.tires || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion id="truck-section-fuel" disableGutters sx={{
                  mb: 2.5,
                  backgroundColor: '#e8f5e9',
                  borderRadius: 2,
                  border: '2px solid #4caf50',
                  overflow: 'hidden',
                  '&:before': { display: 'none' }
                }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#2e7d32' }} />} sx={{ px: 2.5, py: 1, color: '#2e7d32' }}>
                    <Typography variant="subtitle1" sx={{
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <LocalGasStationIcon /> Fuel & Cargo
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.fuel_tank_number) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          borderLeft: '3px solid #4caf50'
                        }}>
                          <LocalGasStationIcon sx={{ color: '#4caf50', fontSize: 22 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{
                              color: '#999',
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                              fontWeight: 'bold'
                            }}>
                              Fuel Tank Count
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                              {formatMaybeNumber(truck.fuel_tank_number)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.fuel_tank_type) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          borderLeft: '3px solid #4caf50'
                        }}>
                          <LocalGasStationIcon sx={{ color: '#4caf50', fontSize: 22 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{
                              color: '#999',
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                              fontWeight: 'bold'
                            }}>
                              Fuel Tank Type
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                              {truck.fuel_tank_type || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          borderLeft: '3px solid #4caf50'
                        }}>
                          <LocalGasStationIcon sx={{ color: '#4caf50', fontSize: 22 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{
                              color: '#999',
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                              fontWeight: 'bold'
                            }}>
                              Fuel Tank Capacities
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                              {formatFuelTankCapacities(truck.fuel_tank_capacities)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.box_length) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #4caf50'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Box Length (ft)
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.box_length || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.liftgate) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #4caf50'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Liftgate
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.liftgate || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.liftgate_status) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #4caf50'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Liftgate Status
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.liftgate_status || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion id="truck-section-lighting" disableGutters sx={{
                  mb: 2.5,
                  backgroundColor: '#fffde7',
                  borderRadius: 2,
                  border: '2px solid #fbc02d',
                  overflow: 'hidden',
                  '&:before': { display: 'none' }
                }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#8d6e00' }} />} sx={{ px: 2.5, py: 1, color: '#8d6e00' }}>
                    <Typography variant="subtitle1" sx={{
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <LightbulbIcon /> Lighting & Electrical
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          borderLeft: '3px solid #fbc02d'
                        }}>
                          <LightbulbIcon sx={{ color: '#fbc02d', fontSize: 22 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{
                              color: '#999',
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                              fontWeight: 'bold'
                            }}>
                              Headlight Type
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                              {resolveOther(truck.headlight_type, truck.headlight_type_other)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          borderLeft: '3px solid #fbc02d'
                        }}>
                          <CalendarTodayIcon sx={{ color: '#fbc02d', fontSize: 22 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{
                              color: '#999',
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                              fontWeight: 'bold'
                            }}>
                              Headlights Design Year
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                              {resolveOther(truck.headlights_design_year, truck.headlights_design_year_other)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #fbc02d'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Interior Light Control Location
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {resolveOther(truck.interior_light_control_location, truck.interior_light_control_location_other)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #fbc02d'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Taillight Design
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {resolveOther(truck.taillightDesign, truck.taillightDesign_other)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #fbc02d'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Taillight Type
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {resolveOther(truck.taillightType, truck.taillightType_other)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #fbc02d'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Boxlight Design
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {resolveOther(truck.boxlightDesign, truck.boxlightDesign_other)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.boxlightType) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #fbc02d'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Boxlight Type
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.boxlightType || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.lightbar) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #fbc02d'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Lightbar
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.lightbar || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.tricklecharger) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #fbc02d'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Trickle Charger Installed?
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.tricklecharger || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.isolatorSwitch) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #fbc02d'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Battery Disconnect Switch Present?
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.isolatorSwitch || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.headlight_condition) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #fbc02d'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Headlight Condition
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.headlight_condition || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.taillight_condition) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #fbc02d'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Taillight Condition
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.taillight_condition || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.boxlight_condition) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #fbc02d'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Boxlight Condition
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.boxlight_condition || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion id="truck-section-condition" disableGutters sx={{
                  mb: 2.5,
                  backgroundColor: '#fce4ec',
                  borderRadius: 2,
                  border: '2px solid #e91e63',
                  overflow: 'hidden',
                  '&:before': { display: 'none' }
                }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#c2185b' }} />} sx={{ px: 2.5, py: 1, color: '#c2185b' }}>
                    <Typography variant="subtitle1" sx={{
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <WarningIcon /> Condition Notes
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.frame_condition) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #e91e63'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Frame
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.frame_condition || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.cab_condition) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #e91e63'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Cab
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.cab_condition || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.rear_door_condition) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #e91e63'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Rear Door
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.rear_door_condition || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: showField(truck.hood_condition) ? 'block' : 'none' }}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 1.5,
                          borderLeft: '3px solid #e91e63'
                        }}>
                          <Typography variant="caption" sx={{
                            color: '#999',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 'bold'
                          }}>
                            Hood
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#001f3f', fontWeight: 'bold', mt: 0.5 }}>
                            {truck.hood_condition || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
                {truck.invoice_url && (
                  <Paper sx={{
                    p: 2,
                    mt: 2,
                    backgroundColor: '#e8f5e9',
                    borderRadius: 2,
                    border: '2px solid #4caf50'
                  }}>
                    <InvoiceAttachmentList value={truck.invoice_url} title="Vehicle Invoice Attachments" />
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid id="truck-section-parts" item xs={12} sx={{ flexBasis: '100%', maxWidth: '100%', width: '100%' }}>
            <Card sx={{
              borderRadius: { xs: 2.5, md: 4 },
              boxShadow: '0 18px 45px rgba(0,31,63,0.14)',
              border: '1px solid #d8e2ef',
              overflow: 'hidden',
              width: '100%'
            }}>
              <CardContent sx={{ p: { xs: 1.15, sm: 1.35, md: 1.55 } }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: { xs: 1, sm: 1.25 },
                  flexDirection: { xs: 'column', sm: 'row' },
                  mb: 1.5
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    <Box sx={{
                      width: { xs: 40, sm: 46 },
                      height: { xs: 40, sm: 46 },
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #001f3f 0%, #1976d2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.5,
                      flexShrink: 0
                    }}>
                      <BuildIcon sx={{ color: 'white', fontSize: { xs: 23, sm: 27 } }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h4" sx={{ fontWeight: 900, color: '#001f3f', fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' }, lineHeight: 1.05, mb: 0.1 }}>
                        Vehicle Parts
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 700, fontSize: { xs: '0.68rem', sm: '0.76rem' }, lineHeight: 1.25 }}>
                        {filteredParts.length} shown • {parts.length} total attached to this vehicle
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setAddPartDialogOpen(true)}
                    fullWidth={partsCardView}
                    sx={{
                      borderRadius: 1.6,
                      background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
                      fontWeight: 'bold',
                      px: 1.55,
                      py: 0.62,
                      minHeight: 36,
                      fontSize: { xs: '0.78rem', sm: '0.86rem' },
                      '&:hover': { background: 'linear-gradient(135deg, #003d7a 0%, #001f3f 100%)' }
                    }}
                  >
                    Add Part
                  </Button>
                </Box>

                <Paper sx={{
                  mb: 3,
                  p: { xs: 1.5, sm: 2 },
                  display: 'flex',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  borderRadius: 3,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0'
                }}>
                  <TextField
                    placeholder="Search by SKU, type, location, OEM, or serial..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size={partsCardView ? 'medium' : 'small'}
                    sx={{ flex: 1, minWidth: { xs: '100%', md: 360 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl size={partsCardView ? 'medium' : 'small'} sx={{ minWidth: { xs: '100%', sm: 180 }, flex: { xs: 1, sm: 'none' } }}>
                    <Select
                      value={filterCondition}
                      onChange={(e) => setFilterCondition(e.target.value)}
                      displayEmpty
                      startAdornment={
                        <InputAdornment position="start">
                          <FilterListIcon fontSize="small" />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="all">All Conditions</MenuItem>
                      <MenuItem value="excellent">Excellent (5)</MenuItem>
                      <MenuItem value="good">Good (3-4)</MenuItem>
                      <MenuItem value="fair">Fair (1-2)</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size={partsCardView ? 'medium' : 'small'} sx={{ minWidth: { xs: '100%', sm: 150 }, flex: { xs: 1, sm: 'none' } }}>
                    <Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="new">New</MenuItem>
                      <MenuItem value="used">Used</MenuItem>
                    </Select>
                  </FormControl>
                </Paper>

                <Divider sx={{ mb: 2 }} />

                {filteredParts.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: { xs: 6, md: 8 }, backgroundColor: '#f8fafc', borderRadius: 3 }}>
                    <InfoIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                      No parts found matching your filters
                    </Typography>
                  </Box>
                ) : partsCardView ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {filteredParts.map((part) => (
                      <VehiclePartCard
                        key={part.id}
                        part={part}
                        allTrucks={allTrucks}
                        onAssign={handleAssignPart}
                        onEdit={(partToEdit) => {
                          setSelectedPart(partToEdit);
                          setEditPartDialogOpen(true);
                        }}
                        onDelete={(partToDelete) => setDeletePart(partToDelete)}
                        getTruckName={getTruckName}
                        getConditionColor={getConditionColor}
                        getConditionLabel={getConditionLabel}
                      />
                    ))}
                  </Box>
                ) : (
                  <TableContainer sx={{
                    overflowX: 'auto',
                    maxHeight: '72vh',
                    borderRadius: 3,
                    border: '1px solid #d8e2ef',
                    boxShadow: 'inset 0 0 0 1px rgba(0,31,63,0.02)'
                  }}>
                    <Table stickyHeader size="medium" sx={{ minWidth: 1280 }}>
                      <TableHead sx={{
                        '& .MuiTableCell-head': {
                          backgroundColor: '#001f3f',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          py: 1.75
                        }
                      }}>
                        <TableRow sx={{ backgroundColor: '#001f3f' }}>
                          <TableCell sx={{ width: 56 }} />
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'sku'}
                              direction={orderBy === 'sku' ? order : 'asc'}
                              onClick={() => handleRequestSort('sku')}
                              sx={{
                                color: 'white !important',
                                '&.Mui-active': { color: '#00ff97 !important' },
                                '& .MuiTableSortLabel-icon': { color: '#00ff97 !important' }
                              }}
                            >
                              SKU
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'part_type'}
                              direction={orderBy === 'part_type' ? order : 'asc'}
                              onClick={() => handleRequestSort('part_type')}
                              sx={{
                                color: 'white !important',
                                '&.Mui-active': { color: '#00ff97 !important' },
                                '& .MuiTableSortLabel-icon': { color: '#00ff97 !important' }
                              }}
                            >
                              Part Type
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'part_number_oem'}
                              direction={orderBy === 'part_number_oem' ? order : 'asc'}
                              onClick={() => handleRequestSort('part_number_oem')}
                              sx={{
                                color: 'white !important',
                                '&.Mui-active': { color: '#00ff97 !important' },
                                '& .MuiTableSortLabel-icon': { color: '#00ff97 !important' }
                              }}
                            >
                              OEM Part #
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'serial_number'}
                              direction={orderBy === 'serial_number' ? order : 'asc'}
                              onClick={() => handleRequestSort('serial_number')}
                              sx={{
                                color: 'white !important',
                                '&.Mui-active': { color: '#00ff97 !important' },
                                '& .MuiTableSortLabel-icon': { color: '#00ff97 !important' }
                              }}
                            >
                              Serial #
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOnIcon sx={{ fontSize: 18 }} />
                              <TableSortLabel
                                active={orderBy === 'location'}
                                direction={orderBy === 'location' ? order : 'asc'}
                                onClick={() => handleRequestSort('location')}
                                sx={{
                                  color: 'white !important',
                                  '&.Mui-active': { color: '#00ff97 !important' },
                                  '& .MuiTableSortLabel-icon': { color: '#00ff97 !important' }
                                }}
                              >
                                Location
                              </TableSortLabel>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <TableSortLabel
                              active={orderBy === 'quantity'}
                              direction={orderBy === 'quantity' ? order : 'asc'}
                              onClick={() => handleRequestSort('quantity')}
                              sx={{
                                color: 'white !important',
                                '&.Mui-active': { color: '#00ff97 !important' },
                                '& .MuiTableSortLabel-icon': { color: '#00ff97 !important' }
                              }}
                            >
                              Qty
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center">
                            <TableSortLabel
                              active={orderBy === 'condition_scale'}
                              direction={orderBy === 'condition_scale' ? order : 'asc'}
                              onClick={() => handleRequestSort('condition_scale')}
                              sx={{
                                color: 'white !important',
                                '&.Mui-active': { color: '#00ff97 !important' },
                                '& .MuiTableSortLabel-icon': { color: '#00ff97 !important' }
                              }}
                            >
                              Condition
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="right">
                            <TableSortLabel
                              active={orderBy === 'current_value'}
                              direction={orderBy === 'current_value' ? order : 'asc'}
                              onClick={() => handleRequestSort('current_value')}
                              sx={{
                                color: 'white !important',
                                '&.Mui-active': { color: '#00ff97 !important' },
                                '& .MuiTableSortLabel-icon': { color: '#00ff97 !important' }
                              }}
                            >
                              Value
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Extracted From</TableCell>
                          <TableCell>Assign To</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredParts.map((part) => (
                          <PartRow
                            key={part.id}
                            part={part}
                            allTrucks={allTrucks}
                            onAssign={handleAssignPart}
                            onEdit={(partToEdit) => {
                              setSelectedPart(partToEdit);
                              setEditPartDialogOpen(true);
                            }}
                            onDelete={(partToDelete) => setDeletePart(partToDelete)}
                            getTruckName={getTruckName}
                            getConditionColor={getConditionColor}
                            getConditionLabel={getConditionLabel}
                          />
                        ))}

                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={importantNotesDialogOpen} onClose={() => setImportantNotesDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#001f3f' }}>Notes</Typography>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800 }}>
              {importantNotesSaving ? 'Saving…' : ((importantNotes || '') === (lastSavedImportantNotesRef.current || '') ? 'Saved' : 'Typing…')}
            </Typography>
          </Box>
          <IconButton onClick={() => setImportantNotesDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            value={importantNotes}
            onChange={(event) => setImportantNotes(event.target.value)}
            placeholder="Write vehicle overview notes here..."
            fullWidth
            multiline
            minRows={8}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="contained" onClick={() => setImportantNotesDialogOpen(false)} sx={{ bgcolor: '#001f3f', fontWeight: 900 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={idiosyncrasiesDialogOpen} onClose={() => setIdiosyncrasiesDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#6a1b9a' }}>Idiosyncrasies</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(106,27,154,0.9)', fontWeight: 800 }}>
              {idiosyncrasySaving ? 'Saving...' : 'Saved'}
            </Typography>
          </Box>
          <IconButton onClick={() => setIdiosyncrasiesDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {idiosyncrasyItems.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No items yet.</Typography>
            ) : (
              idiosyncrasyItems.map((it, idx) => (
                <Box key={`${it.text}-${idx}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1, borderRadius: 2, bgcolor: '#faf5ff' }}>
                  <Checkbox
                    size="small"
                    checked={!!it.checked}
                    onChange={() => setIdiosyncrasyItems((prev) => prev.map((p, i) => (i === idx ? { ...p, checked: !p.checked } : p)))}
                    sx={{ mt: 0.25 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      whiteSpace: 'pre-wrap',
                      textDecoration: it.checked ? 'line-through' : 'none',
                      color: it.checked ? 'text.secondary' : '#001f3f',
                      pt: 0.65,
                    }}
                  >
                    {it.text}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setIdiosyncrasyItems((prev) => prev.filter((_, i) => i !== idx))}
                    aria-label="Delete idiosyncrasy item"
                    sx={{ mt: 0.15 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              size="small"
              placeholder="Add an item..."
              value={idiosyncrasyDraft}
              onChange={(e) => setIdiosyncrasyDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const text = idiosyncrasyDraft.trim();
                  if (!text) return;
                  setIdiosyncrasyItems((prev) => [...prev, { checked: false, text }]);
                  setIdiosyncrasyDraft('');
                }
              }}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={() => {
                const text = idiosyncrasyDraft.trim();
                if (!text) return;
                setIdiosyncrasyItems((prev) => [...prev, { checked: false, text }]);
                setIdiosyncrasyDraft('');
              }}
              startIcon={<AddIcon />}
              sx={{ borderRadius: 2, backgroundColor: '#6a1b9a', '&:hover': { backgroundColor: '#4a148c' }, fontWeight: 900 }}
            >
              Add
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="contained" onClick={() => setIdiosyncrasiesDialogOpen(false)} sx={{ bgcolor: '#6a1b9a', fontWeight: 900 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        fullScreen={photoDialogFullScreen}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: photoDialogFullScreen ? 0 : 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <LocalShippingIcon />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {truck?.name || 'Truck'} — Photo
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {dialogPhotoUrl && (
              <IconButton
                color="inherit"
                onClick={() => window.open(dialogPhotoUrl, '_blank')}
                sx={{ backgroundColor: 'rgba(255,255,255,0.08)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' } }}
                size="small"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            )}
            {canDeleteDialogPhoto && (
              <IconButton
                color="inherit"
                onClick={() => handleDeleteActivePhoto(dialogPhoto)}
                sx={{ backgroundColor: 'rgba(255,255,255,0.08)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' } }}
                size="small"
                aria-label="Delete photo"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              color="inherit"
              onClick={() => setPhotoZoom((z) => Math.min(4, Number((z + 0.25).toFixed(2))))}
              sx={{ backgroundColor: 'rgba(255,255,255,0.08)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' } }}
              size="small"
              aria-label="Zoom in"
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={() => setPhotoZoom((z) => Math.max(1, Number((z - 0.25).toFixed(2))))}
              sx={{ backgroundColor: 'rgba(255,255,255,0.08)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' } }}
              size="small"
              aria-label="Zoom out"
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={() => setPhotoZoom(1)}
              sx={{ backgroundColor: 'rgba(255,255,255,0.08)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' } }}
              size="small"
              aria-label="Reset zoom"
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => setPhotoDialogOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: '#0b1b2a' }}>
          <Box
            sx={{
              width: '100%',
              height: photoDialogFullScreen ? 'calc(100vh - 64px)' : '70vh',
              overflow: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2
            }}
          >
            {dialogPhotoUrl ? (
              <Box
                component="img"
                src={dialogPhotoUrl}
                alt="Truck full"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  transform: `scale(${photoZoom})`,
                  transformOrigin: 'center center',
                  transition: 'transform 120ms ease-out',
                  cursor: photoZoom > 1 ? 'move' : 'zoom-in'
                }}
                onClick={() => setPhotoZoom((z) => (z === 1 ? 1.5 : 1))}
              />
            ) : (
              <Box sx={{ p: 3, color: 'white' }}>
                <Typography>No photo available.</Typography>
              </Box>
            )}
          </Box>
          {resolvedPhotos.length > 1 && (
            <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {resolvedPhotos.slice(0, 20).map((p, idx) => (
                <Box
                  key={`${p.id}-dialog-${idx}`}
                  component="img"
                  src={p.url}
                  alt={`Truck ${idx + 1}`}
                  onClick={() => { setPhotoDialogIndex(idx); setActivePhotoIndex(idx); setPhotoZoom(1); }}
                  sx={{
                    width: 68,
                    height: 52,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: idx === photoDialogIndex ? '2px solid #00ff97' : '1px solid rgba(255,255,255,0.25)',
                    cursor: 'pointer'
                  }}
                />
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <EditTruckDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        truck={truck}
        onTruckUpdated={handleTruckUpdated}
      />

      <AddPartDialog
        open={addPartDialogOpen}
        onClose={() => setAddPartDialogOpen(false)}
        onPartAdded={handleVehiclePartAdded}
        defaultUnitIntendedFor={truck?.id || ''}
        hideVehicleAssignment
      />

      <EditPartDialog
        open={editPartDialogOpen}
        onClose={() => {
          setEditPartDialogOpen(false);
          setSelectedPart(null);
        }}
        part={selectedPart}
        onPartUpdated={handleVehiclePartUpdated}
        hideVehicleAssignment
      />

      <ConfirmDialog
        open={archiveConfirmOpen}
        title={truck?.archived ? 'Unarchive Truck?' : 'Archive Truck?'}
        description={truck?.archived
          ? 'This will bring the truck back into your primary lists.'
          : 'This will hide the truck from your primary lists, but keep it in the database.'}
        confirmText={archiving ? (truck?.archived ? 'Unarchiving…' : 'Archiving…') : (truck?.archived ? 'Unarchive' : 'Archive')}
        confirmColor="warning"
        onConfirm={async () => { await handleToggleArchive(); setArchiveConfirmOpen(false); }}
        onClose={() => setArchiveConfirmOpen(false)}
        loading={archiving}
        icon={truck?.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
      />

      <ConfirmDialog
        open={Boolean(deletePart)}
        title="Delete Part?"
        description={`Archive "${deletePart?.part_type || deletePart?.sku || 'this part'}"? It will be removed from active inventory but preserved for recovery.`}
        confirmText={deletingPart ? 'Deleting…' : 'Delete Part'}
        confirmColor="error"
        onConfirm={handleDeleteVehiclePart}
        onClose={() => { if (!deletingPart) setDeletePart(null); }}
        loading={deletingPart}
        icon={<DeleteIcon color="error" />}
      />
      {/* ================= INVOICE PREVIEW MODAL ================= */}
      <Dialog
        open={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Invoice Preview
          <IconButton
            onClick={() => setInvoiceOpen(false)}
            sx={{ float: 'right' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {truck?.invoice_url ? (
            <InvoiceAttachmentList value={truck.invoice_url} title="Vehicle Invoice Attachments" />
          ) : (
            <Typography>No invoice attached to this truck.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
